import jsQR from 'jsqr';

/**
 * QR decoding for the kiosk scanner.
 *
 * Two backends, picked once per scanner session:
 *   1. Native BarcodeDetector — hardware accelerated, available on Chrome
 *      (Android / Windows kiosks). Reads straight from the <video>.
 *   2. jsQR — pure JS fallback for everything else (notably iOS Safari, which
 *      has no BarcodeDetector). Frames go through a canvas first.
 */

// jsQR walks every pixel, so full-resolution frames are wasteful on a tablet.
// 640px on the long edge still decodes a QR held up to the camera.
const MAX_SCAN_WIDTH = 640;

async function createNativeDecoder() {
    if (!('BarcodeDetector' in window)) return null;

    try {
        const formats = await window.BarcodeDetector.getSupportedFormats();
        if (!formats.includes('qr_code')) return null;

        const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
        return async (video) => {
            const [code] = await detector.detect(video);
            return code?.rawValue || null;
        };
    } catch (e) {
        // Some builds expose the constructor but throw on use — fall back.
        return null;
    }
}

function createCanvasDecoder() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    return async (video) => {
        const scale = Math.min(1, MAX_SCAN_WIDTH / video.videoWidth);
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(frame.data, frame.width, frame.height, {
            inversionAttempts: 'dontInvert', // printed QR codes are dark-on-light
        });
        return code?.data || null;
    };
}

/**
 * Returns `async (videoEl) => string | null` — the decoded QR payload, or null
 * when the current frame holds no readable code.
 */
export async function createQrDecoder() {
    return (await createNativeDecoder()) || createCanvasDecoder();
}
